import { useMemo } from "react";

import { getDatabaseType } from "app/components/appwizard/component-wizards/wizards/common/databaseTypes";
import { DB_PROVIDERS } from "app/components/appwizard/component-wizards/wizards/source/database-reader/configs/db-providers";
import {
    TableWrapper,
    WhatIsTable
} from "app/components/appwizard/component-wizards/wizards/source/database-reader/configs/db-providers.utils";
import utils from "core/utils";
import { CurrentSelection, SourceInfo } from "./select-tables-context";
import dateLib, { timezone } from "core/utils/date-utils";
import { Schema } from "./select-tables-modal/schema-table";

const TABLE_SELECTION_SUPPORTED_SOURCES = [
    "IncrementalBatchReader",
    "DatabaseReader",
    "OracleReader",
    "MSSqlReader",
    "MysqlReader",
    "PostgreSQLReader",
    "OJet",
    "MSJet"
];

const TABLE_SELECTION_SUPPORTED_DB_PROVIDERS = [
    "Postgres",
    "CloudSqlPostgres",
    "Oracle",
    "MySQL",
    "CloudSqlMySQL",
    "SQLServer",
    "CloudSqlSQLServer"
];

const ADAPTERS_WITH_PROVIDER_TYPE = ["DATABASEREADER", "INCREMENTALBATCHREADER"];

const useSelectTableConfigs = (handler: string, initialValues: object) => {
    return useMemo(() => {
        const adapterName: string = handler ? utils.getName(handler) : "";

        const dbType = adapterName === "YugabyteReader" ? DB_PROVIDERS.YUGABYTE.id : getDatabaseType(adapterName);
        const databaseProviderType =
            initialValues?.DatabaseProviderType && initialValues?.DatabaseProviderType !== "Default"
                ? initialValues?.DatabaseProviderType.toUpperCase()
                : null;

        const { whatIsTable: entityType, tableWrapper: schemaType } = DB_PROVIDERS[databaseProviderType || dbType] || {
            whatIsTable: WhatIsTable.TABLE,
            tableWrapper: TableWrapper.SCHEMA
        };

        const isCDC = !["DatabaseReader", "SalesForceReader"].includes(adapterName);

        return {
            adapterName,
            dbType,
            schemaType: getSchemaTypeFromDBType(dbType, schemaType, isCDC),
            entityType: getEntityTypeFromDBType(dbType, entityType),
            isCDC
        };
    }, [handler, initialValues]);
};

const getEntityTypeFromDBType = (dbType: string, defaultEntityType = "Table"): WhatIsTable => {
    switch (dbType) {
        case DB_PROVIDERS.GOOGLEADS.id:
            return "Table" as WhatIsTable;
        default:
            return defaultEntityType as WhatIsTable;
    }
};

const getSchemaTypeFromDBType = (dbType: string, defaultSchemaType = "Schema", isCDC = false): TableWrapper => {
    switch (dbType) {
        case DB_PROVIDERS.SALESFORCE.id:
            if (isCDC) return "Channel" as TableWrapper;
            else return defaultSchemaType as TableWrapper;
        default:
            return defaultSchemaType as TableWrapper;
    }
};

const getCurrentDbType = (dbType: string, databaseProviderType?: string) => {
    return dbType === "MSSQL" /* DB_PROVIDERS has no MSSQL */
        ? "SQLSERVER"
        : ADAPTERS_WITH_PROVIDER_TYPE.includes(dbType) && !!databaseProviderType
        ? getDatabaseType(databaseProviderType)
        : dbType;
};

const getStatusText = (sourceInfo: SourceInfo, currentSelection: CurrentSelection): string => {
    const { isAppAdapter, dbType, schemaType, entityType, isCDC } = sourceInfo;
    const { schemas, entities } = currentSelection;

    if (isAppAdapter && ((dbType === DB_PROVIDERS.SALESFORCE.id && !isCDC) || dbType !== DB_PROVIDERS.SALESFORCE.id)) {
        return `${utils.pluralize(schemaType.toLowerCase(), schemas)} selected`;
    }
    return `${utils.pluralize(entityType.toLowerCase(), entities)} selected in ${utils.pluralize(
        schemaType.toLowerCase(),
        schemas
    )}`;
};

const getFormattedDate = (timestamp: number) => {
    const date = dateLib(timestamp);
    return `${date.format("ll")} at ${date.format("h:mm A")} ${timezone}`;
};

/**
 * Splits a qualified name by dots, but only on dots that are outside double quotes.
 * e.g. '"my.schema"."table"' => ['"my.schema"', '"table"']
 *      'schema.table' => ['schema', 'table']
 *      'CDB$ROOT."C##QATEST".ojet_src' => ['CDB$ROOT', '"C##QATEST"', 'ojet_src']
 */
const splitByDotOutsideQuotes = (entry: string): string[] => {
    const parts: string[] = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < entry.length; i++) {
        const char = entry[i];
        if (char === '"') {
            inQuotes = !inQuotes;
            current += char;
        } else if (char === "." && !inQuotes) {
            parts.push(current);
            current = "";
        } else {
            current += char;
        }
    }
    if (current) {
        parts.push(current);
    }
    return parts;
};

/**
 * Reconstructs the `data` (Schema[]) from the current tables string value (props.value),
 * so that the full data does not need to be persisted to indexedDB.
 *
 * Handles formats like:
 *   schema.table1;schema.table2
 *   "NUHCMES"."ANLAGE";"NUHCMES"."FAG"
 *   CDB$ROOT."C##QATEST".ojet_src
 */
const buildDataFromTablesValue = (
    tablesValue: string
): { data: Schema[]; schemas: number; entities: number; containerName?: string } => {
    if (!tablesValue) {
        return { data: [], schemas: 0, entities: 0 };
    }

    const tableEntries = tablesValue
        .split(";")
        .map(s => s.trim())
        .filter(Boolean);
    const schemaMap = new Map<string, { name: string; fullTableName: string }[]>();

    const stripQuotes = (s: string) => (s.startsWith('"') && s.endsWith('"') ? s.slice(1, -1) : s);

    let containerName: string | undefined;

    for (const entry of tableEntries) {
        const parts = splitByDotOutsideQuotes(entry);

        if (parts.length >= 3 && !containerName) {
            containerName = stripQuotes(parts[0]);
        }

        const schemaName = stripQuotes(parts.length > 1 ? parts[parts.length - 2] : parts[0]);
        const tableName = stripQuotes(parts.length > 1 ? parts[parts.length - 1] : parts[0]);

        if (!schemaMap.has(schemaName)) {
            schemaMap.set(schemaName, []);
        }
        schemaMap.get(schemaName).push({
            name: tableName,
            fullTableName: entry
        });
    }

    let schemaId = 0;
    let totalEntities = 0;
    const schemas: Schema[] = [];

    for (const [schemaName, tables] of schemaMap) {
        totalEntities += tables.length;
        schemas.push({
            id: schemaId++,
            name: schemaName,
            isDisabled: false,
            isSelected: true,
            tables: tables.map((t, index) => ({
                id: `${schemaName}-${index}`,
                name: t.name,
                fullTableName: t.fullTableName,
                isSelected: true
            }))
        });
    }

    return { data: schemas, schemas: schemas.length, entities: totalEntities, ...(containerName && { containerName }) };
};

const parseMultiQueryToQueryItems = (
    multiQueryValue: string
): { id: string; identifier: string; sql: string; priority: number }[] | null => {
    if (!multiQueryValue?.trim()) return null;
    try {
        const parsed = JSON.parse(multiQueryValue);
        if (!Array.isArray(parsed) || parsed.length === 0) return null;
        return parsed.map((q, index) => ({
            id: `query-${index}`,
            identifier: q.QueryIdentifier,
            sql: q.Query,
            priority: q.Priority ?? index + 1
        }));
    } catch (e) {
        console.error("Failed to parse MultiQuery value", e);
        return null;
    }
};

const parseSingleQueryToQueryItems = (
    queryValue: string
): { id: string; identifier: string; sql: string; priority: number }[] | null => {
    if (!queryValue?.trim()) return null;
    return [{ id: "query-0", identifier: "QUERY", sql: queryValue, priority: 1 }];
};

export {
    getFormattedDate,
    useSelectTableConfigs,
    TABLE_SELECTION_SUPPORTED_SOURCES,
    TABLE_SELECTION_SUPPORTED_DB_PROVIDERS,
    ADAPTERS_WITH_PROVIDER_TYPE,
    getCurrentDbType,
    getStatusText,
    getEntityTypeFromDBType,
    getSchemaTypeFromDBType,
    buildDataFromTablesValue,
    parseMultiQueryToQueryItems,
    parseSingleQueryToQueryItems
};
